import Phaser from 'phaser';
import { GEAR_SHAPES, type EnhanceAddon, type ShapePart, type ShapePoint, type ShapeTier } from '../data/gearShapes';
import type { ArchetypeId, GearSlot, Rarity } from '../model/types';
import type { GearLayer, HeroFigure } from './heroView';

const ARCHETYPE_COLOR: Record<ArchetypeId, number> = {
  ironbound: 0xc44949,
  veilweaver: 0x8d7dff,
  wildkin: 0x6fbf73,
  shadecaller: 0x2fbfb0,
};

const TRIM: Record<Rarity, number | null> = {
  common: null,
  uncommon: 0x3cba6a,
  rare: 0x4aa3ff,
  epic: 0xb06bff,
  legendary: 0xf0b429,
};

function metal(tier: ShapeTier): { main: number; edge: number } {
  if (tier === 1) return { main: 0xcbb89a, edge: 0x8d6b52 };
  if (tier === 2) return { main: 0xe0a24a, edge: 0x9a5e22 };
  if (tier === 3) return { main: 0xf2f6fb, edge: 0x8ea0b8 };
  return { main: 0x8e7352, edge: 0xf0d7a2 };
}

function glowAlpha(rarity: Rarity, time: number): number {
  if (rarity === 'legendary') return 0.28 + Math.sin(time * 2.6) * 0.14;
  if (rarity === 'epic') return 0.16;
  return 0;
}

/**
 * Code-rig placeholder. Each visible slot is its own draw pass so a later
 * hand-painted atlas can replace `paintLayer` without changing the figure model.
 */
export function drawHero(
  g: Phaser.GameObjects.Graphics,
  figure: HeroFigure,
  cx: number,
  feetY: number,
  scale: number,
  time: number,
  lunge: number,
): void {
  const px = (x: number) => cx + x * scale;
  const py = (y: number) => feetY + y * scale;
  const accent = figure.heart ? ARCHETYPE_COLOR[figure.heart] : 0x8d7a68;

  const rounded = (
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    fill: number,
    layer?: GearLayer,
  ) => {
    if (layer) {
      const trim = TRIM[layer.rarity];
      const glow = glowAlpha(layer.rarity, time);
      if (glow > 0 && trim !== null) {
        g.fillStyle(trim, glow);
        g.fillRoundedRect(px(x - 5), py(y - 5), (w + 10) * scale, (h + 10) * scale, (r + 4) * scale);
      }
      g.fillStyle(fill, 1);
      g.fillRoundedRect(px(x), py(y), w * scale, h * scale, r * scale);
      if (trim !== null) {
        g.lineStyle(Math.max(2, 2.4 * scale), trim, 1);
        g.strokeRoundedRect(px(x), py(y), w * scale, h * scale, r * scale);
        g.lineStyle(0, 0, 0);
      }
      return;
    }
    g.fillStyle(fill, 1);
    g.fillRoundedRect(px(x), py(y), w * scale, h * scale, r * scale);
  };

  const circle = (x: number, y: number, r: number, fill: number, layer?: GearLayer) => {
    if (layer) {
      const trim = TRIM[layer.rarity];
      const glow = glowAlpha(layer.rarity, time);
      if (glow > 0 && trim !== null) {
        g.fillStyle(trim, glow);
        g.fillCircle(px(x), py(y), (r + 6) * scale);
      }
      g.fillStyle(fill, 1);
      g.fillCircle(px(x), py(y), r * scale);
      if (trim !== null) {
        g.lineStyle(Math.max(2, 2.4 * scale), trim, 1);
        g.strokeCircle(px(x), py(y), r * scale);
        g.lineStyle(0, 0, 0);
      }
      return;
    }
    g.fillStyle(fill, 1);
    g.fillCircle(px(x), py(y), r * scale);
  };

  g.fillStyle(0x000000, 0.28);
  g.fillEllipse(px(4), py(-4), 86 * scale, 18 * scale);

  if (figure.averageEnhance >= 8) {
    g.lineStyle(3 * scale, 0xf0b429, 0.45 + Math.sin(time * 3) * 0.15);
    g.strokeEllipse(px(4), py(-2), 96 * scale, 22 * scale);
    g.lineStyle(0, 0, 0);
  }

  if (figure.level >= 5) {
    rounded(-34, -158, 58, 132, 16, 0x4a3048);
    g.fillStyle(accent, 0.95);
    g.fillRect(px(-6), py(-150), 5 * scale, 96 * scale);
  }

  rounded(-16, -96, 15, 82, 6, 0x6a5348);
  rounded(4, -100, 16, 86, 6, 0x7c6254);

  paintSlot(g, 'boots', figure.layers.boots, px, py, scale, time);

  rounded(-28, -158, 58, 70, 14, 0x8a6a72);
  g.fillStyle(accent, 1);
  g.fillRoundedRect(px(-8), py(-150), 14 * scale, 46 * scale, 4 * scale);

  paintSlot(g, 'chest', figure.layers.chest, px, py, scale, time);
  paintSlot(g, 'offhand', figure.layers.offhand, px, py, scale, time);

  circle(8, -186, 22, 0xf0c8a8);
  g.fillStyle(0x2a2030, 1);
  g.fillCircle(px(16), py(-188), 2.4 * scale);
  paintSlot(g, 'helm', figure.layers.helm, px, py, scale, time);

  rounded(22 + lunge, -148, 14, 50, 6, 0xf0c8a8);
  paintSlot(g, 'gloves', figure.layers.gloves, px, py, scale, time, lunge);
  paintSlot(g, 'weapon', figure.layers.weapon, px, py, scale, time, lunge);

  if (figure.level >= 10) {
    figure.marks.forEach((mark, index) => {
      const color = mark ? ARCHETYPE_COLOR[mark] : 0x8a7d70;
      circle(-8 + index * 12, -118, 4.5, color);
    });
  }
}

interface PaintedPiece {
  tier: ShapeTier;
  rarity: Rarity;
  enhance: number;
}

function paintSlot(
  g: Phaser.GameObjects.Graphics,
  slot: GearSlot,
  layer: PaintedPiece | undefined,
  px: (x: number) => number,
  py: (y: number) => number,
  scale: number,
  time: number,
  lunge = 0,
): void {
  if (!layer) return;
  const shape = GEAR_SHAPES[slot];
  const tier = layer.tier as ShapeTier;
  const parts = shape.tiers[tier];
  const shift = slot === 'weapon' || slot === 'gloves' ? lunge : 0;
  const place = (points: ShapePoint[], socket?: ShapePoint) =>
    points.map((point) => ({
      x: px(shape.anchor.x + shift + (socket?.x ?? 0) + point.x),
      y: py(shape.anchor.y + (socket?.y ?? 0) + point.y),
    }));
  const palette = metal(tier);
  for (const part of parts) {
    g.fillStyle(part.role === 'body' || part.role === 'plate' || part.role === 'pauldron' ? palette.main : palette.edge, 1);
    g.fillPoints(place(part.points), true);
  }
  const marks = shape.enhance.filter((mark) => layer.enhance >= mark.at);
  for (const mark of marks) fillMark(g, mark, place, palette.edge);
  strokeTrim(g, layer, parts, marks, place, scale, time);
  if (slot === 'weapon' && layer.enhance >= 5) {
    const grip = place([{ x: 4, y: 4 }, { x: 18, y: -36 }]);
    g.lineStyle(2 * scale, 0xfff4cc, 0.85);
    g.lineBetween(grip[0]!.x, grip[0]!.y, grip[1]!.x, grip[1]!.y);
    g.lineStyle(0, 0, 0);
  }
}

function fillMark(
  g: Phaser.GameObjects.Graphics,
  mark: EnhanceAddon,
  place: (points: ShapePoint[], socket?: ShapePoint) => { x: number; y: number }[],
  edge: number,
): void {
  g.fillStyle(mark.role === 'flourish' ? 0xf0b429 : edge, 1);
  g.fillPoints(place(mark.points, mark.socket), true);
}

function strokeTrim(
  g: Phaser.GameObjects.Graphics,
  layer: PaintedPiece,
  parts: ShapePart[],
  marks: EnhanceAddon[],
  place: (points: ShapePoint[], socket?: ShapePoint) => { x: number; y: number }[],
  scale: number,
  time: number,
): void {
  const trim = TRIM[layer.rarity];
  if (trim === null) return;
  const glow = glowAlpha(layer.rarity, time);
  const body = parts.find((part) => part.role === 'body' || part.role === 'plate') ?? parts[0];
  if (glow > 0 && body) {
    g.lineStyle(Math.max(6, 7 * scale), trim, glow);
    g.strokePoints(place(body.points), true);
  }
  g.lineStyle(Math.max(2, 2.4 * scale), trim, 1);
  for (const part of parts) g.strokePoints(place(part.points), true);
  for (const mark of marks) g.strokePoints(place(mark.points, mark.socket), true);
  g.lineStyle(0, 0, 0);
}

/** T1–T4 ladder, then one chest at +0, +3, +6, +9, and +10. Used by ?shapes=1. */
export function drawShapeBoard(g: Phaser.GameObjects.Graphics, width: number, height: number): void {
  g.fillStyle(0x120c16, 1);
  g.fillRect(0, 0, width, height);
  const tiers: ShapeTier[] = [1, 2, 3, 4];
  tiers.forEach((tier, index) => {
    const cx = width * (0.14 + index * 0.24);
    const feet = height * 0.46;
    const px = (x: number) => cx + x * 0.72;
    const py = (y: number) => feet + y * 0.72;
    paintSlot(g, 'boots', { tier, rarity: 'common', enhance: 0 }, px, py, 0.72, 0);
    paintSlot(g, 'chest', { tier, rarity: 'rare', enhance: 0 }, px, py, 0.72, 0);
    paintSlot(g, 'helm', { tier, rarity: 'rare', enhance: 0 }, px, py, 0.72, 0);
    paintSlot(g, 'offhand', { tier, rarity: 'epic', enhance: 0 }, px, py, 0.72, 1);
    paintSlot(g, 'weapon', { tier, rarity: 'legendary', enhance: 0 }, px, py, 0.72, 1);
  });
  const steps = [0, 3, 6, 9, 10];
  steps.forEach((enhance, index) => {
    const cx = width * (0.12 + index * 0.19);
    const feet = height * 0.92;
    const px = (x: number) => cx + x * 0.9;
    const py = (y: number) => feet + y * 0.9;
    paintSlot(g, 'chest', { tier: 2, rarity: 'epic', enhance }, px, py, 0.9, 1);
  });
}

export function slotLabel(slot: GearSlot): string {
  if (slot === 'offhand') return 'Off-hand';
  return slot.charAt(0).toUpperCase() + slot.slice(1);
}
